import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useMemo, useState } from 'react';
import { appointmentActions, sessionActions } from 'store';
import { FindLoad, OrgIntegrations, useSessionSocket } from 'utils';
import { Loader } from 'components';
import { SessionGoalGridTargets, SessionHeader, SessionPinnedTargets, SessionGoalListTargets, SessionPostedNote } from './core';
import { cardViewEnums } from './constants';
import { useNavigate, useParams } from 'react-router-dom';
import { SessionWarning } from './core/sessionWarning';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';

const ACTION_TYPE = 'GET_SESSION_BY_ID';
const APPT_ACTION_TYPE = 'GET_APPOINTMENT_BY_ID';
export const Session = ({}) => {
    const params = useParams();
    const sessionById = useSelector((state) => state.session.sessionById);
    const dispatch = useDispatch();
    const loader = FindLoad(ACTION_TYPE);
    const apptLoader = FindLoad(APPT_ACTION_TYPE);
    const { cSession } = OrgIntegrations();
    const navigate = useNavigate();

    useSessionSocket(cSession ? params?.sessionId : null);
    const [filters, setFilters] = useState({
        viewType: cardViewEnums.LIST,
    });

    useEffect(() => {
        if (params?.sessionId && params?.apptId) {
            dispatch(appointmentActions.getAppointmentById(params?.apptId));
            dispatch(sessionActions.getSessionById(params?.sessionId));
        } else {
            navigate(`/dashboard`);
        }
    }, []);

    const listData = useMemo(() => {
        if (sessionById?.goals) {
            let list = [...sessionById?.goals];
            let searchText = filters?.search?.trim();
            if (searchText) {
                const search = searchText.trim().toLowerCase();
                list = list.filter((item) => item.goal.name.toLowerCase().includes(search));
            }

            const sortBy = filters?.sortBy;
            if (sortBy === 'nameAsc') {
                list.sort((a, b) => a.goal.name.trim().localeCompare(b.goal.name.trim()));
            } else if (sortBy === 'nameDesc') {
                list.sort((a, b) => b.goal.name.trim().localeCompare(a.goal.name.trim()));
            } else if (sortBy === 'mostTrials') {
                list.sort((a, b) => (b.goal?.targets?.length || 0) - (a.goal?.targets?.length || 0));
            } else if (sortBy === 'leastTrials') {
                list.sort((a, b) => (a.goal?.targets?.length || 0) - (b.goal?.targets?.length || 0));
            } else if (filters?.sort) {
                list.sort((a, b) => {
                    const nameA = a.goal.name.trim();
                    const nameB = b.goal.name.trim();
                    return filters?.sort === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
                });
            }

            return list;
        }
        return [];
    }, [sessionById, filters?.sort, filters?.search, filters?.sortBy]);

    const filteredListData = useMemo(() => {
        let arr = [...listData];
        if (filters.domain?.length > 0) {
            arr = arr.filter((item) => {
                let includes = filters.domain.find((d) => d === item.goal.domain.id);
                return Boolean(includes);
            });
        }
        if (filters?.type?.length > 0) {
            arr = arr.filter((item) => filters.type.includes(item.goal.type));
        }
        if (filters?.mode?.length > 0) {
            arr = arr.filter((item) => filters.mode.includes(item.goal.dataType));
        }
        if (filters?.tag?.length > 0) {
            arr = arr.filter((item) => {
                let includes = false;
                item.goal.tags.forEach((t) => {
                    if (!includes && filters.tag.find((tag) => tag === t.id)) {
                        includes = true;
                    }
                });
                return includes;
            });
        }
        return arr;
    }, [listData, filters]);

    // Goals are open by default, so track the collapsed ones: new goals arrive open and the
    // user's choice survives session refreshes and filter changes. Display-only state.
    const [collapsedGoalIds, setCollapsedGoalIds] = useState(() => new Set());
    const listGoalIds = filteredListData.filter((i) => !i?.pinned).map((i) => i?.goal?.id);
    const anyExpanded = listGoalIds.some((id) => !collapsedGoalIds.has(id));

    const toggleGoal = (goalId) => {
        setCollapsedGoalIds((prev) => {
            const next = new Set(prev);
            next.has(goalId) ? next.delete(goalId) : next.add(goalId);
            return next;
        });
    };

    const toggleAll = () => {
        setCollapsedGoalIds((prev) => {
            const next = new Set(prev);
            listGoalIds.forEach((id) => (anyExpanded ? next.add(id) : next.delete(id)));
            return next;
        });
    };

    return (
        <div className="session-page-wrapper">
            {loader?.length || apptLoader?.length ? (
                <Loader height={'90vh'} />
            ) : (
                <>
                    <SessionWarning apptId={params?.apptId} />
                    <SessionHeader setFilters={setFilters} filters={filters} />
                    <SessionPostedNote />
                    <SessionPinnedTargets />

                    {filters?.viewType === cardViewEnums.LIST && !!listGoalIds.length && (
                        <div className="flex-end" style={{ padding: '8px 16px 0' }}>
                            <button type="button" className="goal-toggle-all-btn" onClick={toggleAll}>
                                {anyExpanded ? <UnfoldLessIcon sx={{ fontSize: 18 }} /> : <UnfoldMoreIcon sx={{ fontSize: 18 }} />}
                                {anyExpanded ? 'Collapse all' : 'Expand all'}
                            </button>
                        </div>
                    )}
                    {filters?.viewType === cardViewEnums.LIST ? (
                        <SessionGoalListTargets
                            filteredListData={filteredListData}
                            collapsedGoalIds={collapsedGoalIds}
                            onToggleGoal={toggleGoal}
                        />
                    ) : (
                        <SessionGoalGridTargets filteredListData={filteredListData} />
                    )}
                </>
            )}
        </div>
    );
};
